import { queryOptions, useQuery, UseQueryResult } from '@tanstack/react-query';

import { getApiClient } from '@cast/api-client';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { TrialStatusResponse } from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

import { TRIAL_ENABLED } from './trialEnabled';

const TRIAL_STATUS_QUERY_KEY = 'trial-status';

export const getTrialStatusQueryKey = (organizationId?: string) =>
  [TRIAL_STATUS_QUERY_KEY, organizationId] as const;

const getTrialStatusQueryOptions = (
  organizationId: string | undefined,
  enabled: boolean
) =>
  queryOptions({
    queryKey: getTrialStatusQueryKey(organizationId),
    queryFn: async () => {
      const { data } = await getApiClient().plgTrial.getTrialStatus({
        organizationId: organizationId!,
      });
      return data;
    },
    enabled: makeIsEnabled(enabled, !!organizationId, TRIAL_ENABLED),
  });

type useTrialStatusQueryArgs = {
  data?: TrialStatusResponse;
  isLoading: boolean;
  error: unknown;
  refetch: UseQueryResult<TrialStatusResponse, unknown>['refetch'];
};

export const useTrialStatusQuery = ({
  enabled,
}: {
  enabled: boolean;
}): useTrialStatusQueryArgs => {
  const { isReady: isOrganizationReady, currentOrganization } =
    useOrganizations();

  const { data, isLoading, error, refetch } = useQuery(
    getTrialStatusQueryOptions(
      currentOrganization?.id,
      enabled && isOrganizationReady
    )
  );

  return { data, isLoading, error, refetch };
};
